// Phase 2 (dealers and customers) request and response shapes the web sends and
// reads, per the plan's route list (docs/plans/lens-dealer-portal/omni-links.md, 3.6).
// The fields on Dealer, Order and AutotaskCategoryStatus below are the plan's 3.3
// shapes that the server slices are adding to @phoneware/lens-domain; until those
// land, the web reads them through these types so nothing is invented here.
import type {
  AutotaskCategoryLink,
  AutotaskCategoryStatus,
  AutotaskOwnerSeed,
  BillingReviewItem,
  CustomerInput,
  CustomerSummary,
  Dealer,
  Iso,
  Order,
  OrderNotice,
  StepState,
} from "@phoneware/lens-domain";
import { ApiError } from "./api.js";

/** lens_dealers adds the linked Autotask category and the account-manager seed. */
export type DealerLinks = Dealer & {
  autotaskCategory?: AutotaskCategoryLink | null;
  autotaskOwnerSeed?: AutotaskOwnerSeed | null;
};

export function categoryLinkOf(d: Dealer): AutotaskCategoryLink | null {
  return (d as DealerLinks).autotaskCategory ?? null;
}

export function ownerSeedOf(d: Dealer): AutotaskOwnerSeed | null {
  return (d as DealerLinks).autotaskOwnerSeed ?? null;
}

/** Who most of a category's active customers belong to in Autotask, before any dealer holds it. */
export interface CategoryOwnerPreview {
  resourceId: number;
  name: string;
  companies: number;
  ofCustomers: number;
}

export type CategoryStatus = AutotaskCategoryStatus & { ownerPreview?: CategoryOwnerPreview | null };

/** The order's Phase 2 step: a Prospect becomes a Customer in Autotask after billing. */
export interface CompanyStepView {
  state: StepState;
  fromType: number | null;
  error: string | null;
  attempts: number;
  updatedAt: Iso;
}

export type OrderWithCompany = Order & { company?: CompanyStepView };

/** Orders saved before Phase 2 have no company step and count as done. */
export function companyOf(o: Order): CompanyStepView {
  return (o as OrderWithCompany).company ?? { state: "done", fromType: null, error: null, attempts: 0, updatedAt: o.updatedAt };
}

/** PUT /api/dealers/:id/autotask-category. `expectedDealerId` is the dealer the screen saw holding the category; 409 when that changed. */
export interface DealerCategoryRequest {
  categoryId: number | null;
  expectedDealerId: string | null;
}

/** POST /api/customers: ops name the dealer, a dealer's own people never do. */
export type AddCustomerRequest = CustomerInput & { dealerId?: string };

/** POST /api/customers/assign: keeps billing and createdAt; 409 when the customer's dealer changed. */
export interface AssignCustomerRequestV2 {
  autotaskCompanyId: number;
  dealerId: string;
  ownerEmail: string | null;
  expectedDealerId: string | null;
}

/** What a 409 from POST /api/customers carries when Autotask already has the company. */
export interface DuplicateCompany {
  autotaskCompanyId: number;
  name: string;
  companyType: number;
  categoryId: number | null;
  /** Set when Omni already lists it under a dealer. */
  dealerId: string | null;
}

/** GET /api/onebill/accounts?q= */
export interface OneBillAccountMatch {
  accountNumber: string;
  name: string;
  status: string;
}

/** POST /api/billing-review/:accountNumber */
export type BillingReviewRequest = { action: "link"; autotaskCompanyId: number } | { action: "dismiss" } | { action: "reopen" };

export type BillingReviewResult = BillingReviewItem;

/** Autotask companyType picklist (measured, plan 3.4). */
export const COMPANY_TYPE: Record<number, string> = { 1: "Customer", 2: "Lead", 3: "Prospect", 4: "Dead", 6: "Cancellation", 7: "Vendor", 8: "Partner" };

export function companyTypeName(type: number): string {
  return COMPANY_TYPE[type] ?? `Type ${type}`;
}

// ------------------------------------------------------------------ Phase 5: account numbers, contacts, the notice


/** An RPP account number is 4 to 6 digits (plan 3.2). */
export function isRppNumber(text: string): boolean {
  return /^\d{4,6}$/.test(text.trim());
}

/** Orders signed before Phase 5 have no notice step and count as Phoneware told. */
export function noticeOf(o: Order): OrderNotice {
  return o.notice ?? { state: "done", to: null, sentAt: null, error: null, attempts: 0 };
}

/** The 409 from POST /api/orders/:id/billing-approval when OneBill already has the account number Omni would create. */
export function onebillHasNumber(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body;
  if (!body || typeof body !== "object" || !("accountNumber" in body)) return null;
  return typeof body.accountNumber === "string" && body.accountNumber ? body.accountNumber : null;
}
